import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Clock, MapPin, ShieldCheck } from "lucide-react";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee, SpotlightCard } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function SectorDetail() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(() => api.sector(slug), [slug]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="sector-not-found">
          <p className="font-display font-black uppercase text-4xl text-white">Sector not found</p>
          <Link to="/sectors" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
            View all sectors
          </Link>
        </div>
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const s = data.sector;

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/sectors/${slug}`} schema={faqSchema(data.faqs)} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} image={data.image} />
      <Marquee items={data.sections[1].services.map((sv) => sv.name)} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets}>
          {sec.services && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sector-services-grid">
              {sec.services.map((sv) => (
                <Link key={sv.slug} to={`/services/${sv.slug}`} data-testid={`sector-service-${sv.slug}`} className="block h-full group">
                  <SpotlightCard className="h-full p-5 hover:border-amber/60 transition-colors duration-300">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-amber shrink-0" />
                      <span className="font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors leading-tight">
                        {sv.name}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 leading-relaxed">{sv.short}</p>
                  </SpotlightCard>
                </Link>
              ))}
            </div>
          )}
          {sec.cities && (
            <LinkPills
              items={sec.cities}
              toFn={(c) => `/sectors/${slug}/${c.slug}`}
              labelFn={(c) => `${s.name} Security ${c.name}`}
              testIdPrefix="sector-city"
            />
          )}
        </SectionBlock>
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="sector-insights-section">
        <ChapterTag num="INTEL" title={`${s.name.toUpperCase()} — FROM THE INTELLIGENCE DESK`} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.insights.map((a) => (
            <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`sector-insight-${a.slug}`} className="block group border border-white/10 bg-card hover:border-amber transition-colors duration-300 overflow-hidden">
              <div className="relative h-32 overflow-hidden">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  <span className="text-amber">{a.category}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.minutes} min</span>
                </div>
                <h3 className="mt-3 font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors leading-tight">
                  {a.title}
                </h3>
                <p className="mt-2 font-mono text-[10px] text-gray-600 uppercase tracking-widest">{fmtDate(a.date)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {data.cases.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="sector-cases-section">
          <ChapterTag num="PROOF" title={`${s.name.toUpperCase()} — CLIENT OUTCOMES`} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.cases.map((cs, i) => (
              <SpotlightCard key={i} className="p-6 hover:border-amber/60 transition-colors duration-300" testId={`sector-case-${i}`}>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  <MapPin className="w-3 h-3 text-amber" /> {cs.location} — {cs.client}
                </div>
                <h3 className="mt-4 font-display font-extrabold uppercase tracking-tight text-xl sm:text-2xl text-amber leading-tight">
                  {cs.headline}
                </h3>
                <p className="mt-3 text-xs sm:text-sm text-gray-400 leading-relaxed">{cs.body}</p>
                <div className="mt-4 border-l-2 border-amber pl-4">
                  <p className="text-xs sm:text-sm text-gray-300 italic leading-relaxed">“{cs.quote}”</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">{cs.author}</p>
                </div>
              </SpotlightCard>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-gray-600">
            Representative client engagements — details anonymised
          </p>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="sector-faq-section">
        <ChapterTag num="FAQ" title={`${s.name.toUpperCase()} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="sector-faq" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <ChapterTag num="NEXT" title="OTHER SECTORS" />
        <LinkPills items={data.other_sectors} prefix="/sectors" labelFn={(o) => `${o.name} Security`} testIdPrefix="sector-other" />
      </section>
    </PageShell>
  );
}
